/* pp-stories: nur auf Seiten mit dieser Section geladen (aus sections/pp-stories.liquid ausgelagert) */
if (!customElements.get('pp-stories')) {
  const SEEN_KEY = 'pp-stories-seen';
  const HOLD_MS = 250;
  const SWIPE_PX = 45;
  const CLOSE_PX = 90;
  const WHEEL_PX = 30;
  const WHEEL_LOCK_MS = 450;

  const readSeen = () => {
    try {
      return JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
    } catch (error) {
      return [];
    }
  };

  /* Kurzer Fingerabdruck des Story-Inhalts: aendert er sich, gilt die Story wieder als neu */
  const stampOf = (slides) => {
    const source = slides
      .filter((slide) => !slide.intro)
      .map((slide) => slide.url + '|' + (slide.url ? '' : slide.img || slide.title || ''))
      .join(',');
    let hash = 5381;
    for (let index = 0; index < source.length; index += 1) {
      hash = ((hash << 5) + hash + source.charCodeAt(index)) | 0;
    }
    return (hash >>> 0).toString(36);
  };

  const writeSeen = (list) => {
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-60)));
    } catch (error) {
      /* Nur Komfortfunktion */
    }
  };

  const track = (name, detail) => {
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, detail);
      }
    } catch (error) {
      /* Analytics duerfen nie stoeren */
    }
  };

  /* Bild, das sich bei Ladefehler selbst entfernt: nie ein kaputtes Bild-Symbol */
  const createImage = (src, alt, className) => {
    const img = new Image();
    if (className) img.className = className;
    img.alt = alt || '';
    img.decoding = 'async';
    img.draggable = false;
    img.addEventListener('error', () => img.remove(), { once: true });
    img.src = src;
    return img;
  };

  class PpStories extends HTMLElement {
    /*
     * Alle Listener haengen an einem AbortController: disconnectedCallback baut sie ab, und ein
     * erneutes connectedCallback (z. B. im Theme-Editor) startet sauber neu. Sonst liefen Listener
     * doppelt (animationend -> zweimal next() -> jede zweite Folie wurde uebersprungen).
     */
    connectedCallback() {
      this.teardown();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.list = this.querySelector('.pp-stories__list');
      this.dialog = this.querySelector('[data-viewer]');
      this.storyCache = new Map();
      this.entries = [];

      /* Saison-Korrektur: der Liefer-Kern laedt asynchron (Modul), sonst auf 'pp:delivery-ready' warten */
      const onCoreReady = () => {
        this.applyOccasions();
        this.collectEntries();
        this.openFromHash();
      };
      if (window.ppDelivery) this.applyOccasions();
      else document.addEventListener('pp:delivery-ready', onCoreReady, { once: true, signal });

      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.collectEntries();

      const q = (selector) => this.dialog.querySelector(selector);
      this.refs = {
        bars: q('[data-bars]'),
        avatar: q('[data-avatar]'),
        storyTitle: q('[data-story-title]'),
        backdrop: q('[data-backdrop]'),
        stage: q('[data-stage]'),
        title: q('[data-title]'),
        text: q('[data-text]'),
        deadline: q('[data-deadline]'),
        price: q('[data-price]'),
        cta: q('[data-cta]'),
        ctaLabel: q('[data-cta-label]'),
        all: q('[data-all]'),
        more: q('[data-more]'),
        moreList: q('[data-more-list]'),
        frame: q('[data-frame]'),
        pause: q('[data-pause]'),
        live: q('[data-live]'),
      };

      if (this.list) this.list.addEventListener('click', this.onListClick, { signal });

      this.refs.backdrop.addEventListener(
        'error',
        () => {
          this.refs.backdrop.hidden = true;
        },
        { signal }
      );
      this.refs.avatar.addEventListener(
        'error',
        () => {
          this.refs.avatar.hidden = true;
        },
        { signal }
      );

      q('[data-next]').addEventListener('click', () => this.handleZone(() => this.next()), { signal });
      q('[data-prev]').addEventListener('click', () => this.handleZone(() => this.prev()), { signal });
      q('[data-arrow-next]').addEventListener('click', () => this.next(), { signal });
      q('[data-arrow-prev]').addEventListener('click', () => this.prev(), { signal });
      q('[data-close]').addEventListener('click', () => this.close(), { signal });
      if (this.refs.pause) this.refs.pause.addEventListener('click', () => this.togglePause(), { signal });
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog) this.close();
        },
        { signal }
      );
      this.dialog.addEventListener(
        'close',
        () => {
          this.userPaused = false;
          this.holding = false;
          this.focusPaused = false;
          this.syncPause();
          if (this.refs.live) this.refs.live.textContent = '';
          /* Absicherung fuer Escape: close() raeumt sonst schon selbst auf */
          this.releaseHistory();
        },
        { signal }
      );
      this.dialog.addEventListener('keydown', this.onKeyDown, { signal });
      this.dialog.addEventListener(
        'keyup',
        (event) => {
          /* Verhindert, dass die Leertaste zusaetzlich den fokussierten Button ausloest */
          if ((event.key === ' ' || event.key === 'Spacebar') && !this.reduceMotion && event.target !== this.refs.pause) {
            event.preventDefault();
          }
        },
        { signal }
      );
      this.dialog.addEventListener('wheel', this.onWheel, { passive: true, signal });
      this.dialog.addEventListener('focusin', this.onFocusChange, { signal });
      this.dialog.addEventListener('focusout', this.onFocusChange, { signal });

      this.refs.bars.addEventListener('animationend', () => this.next(), { signal });
      this.refs.frame.addEventListener('pointerdown', this.onPointerDown, { signal });
      this.refs.frame.addEventListener('pointerup', this.onPointerUp, { signal });
      this.refs.frame.addEventListener('pointercancel', this.onPointerCancel, { signal });
      this.refs.frame.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

      this.refs.cta.addEventListener(
        'click',
        () => {
          const entry = this.entries[this.current];
          if (entry) track('pp_story_product_click', { story: entry.story.key, url: this.refs.cta.getAttribute('href') });
        },
        { signal }
      );

      this.added = this.added || new Set();
      this.refs.add = q('[data-add]');
      this.refs.addLabel = q('[data-add-label]');
      this.refs.badges = q('[data-badges]');
      this.refs.add.addEventListener('click', () => this.addToCart(), { signal });

      window.addEventListener('hashchange', () => this.openFromHash(), { signal });
      /* Zurueck-Taste/-Geste (Handy): schliesst nur die Story statt die Seite zu verlassen */
      window.addEventListener(
        'popstate',
        (event) => {
          if (this.dialog.open && !(event.state && event.state.ppStory)) {
            /* Der Browser ist schon zurueck: nichts mehr zuruecknehmen */
            this.historyPushed = false;
            this.dialog.close();
          }
        },
        { signal }
      );

      /* Erste Folie vorladen, sobald jemand auf einen Kreis zielt (Maus, Finger, Tastatur) */
      this.preloaded = new WeakSet();
      if (this.list) {
        this.list.addEventListener('pointerover', this.onListIntent, { signal });
        this.list.addEventListener('touchstart', this.onListIntent, { passive: true, signal });
        this.list.addEventListener('focusin', this.onListIntent, { signal });
      }

      this.openFromHash();
      this.loadRecent();
    }

    /* Beim Oeffnen einen Verlaufseintrag anlegen, damit "Zurueck" die Story schliesst */
    pushHistory(key) {
      if (window.Shopify && window.Shopify.designMode) return;
      const hash = `#story=${encodeURIComponent(key)}`;
      if (window.location.hash === hash) {
        /* Per Link oder Vorwaerts-Taste geoeffnet: nur zuruecknehmen, was wir selbst angelegt haben */
        this.historyPushed = Boolean(history.state && history.state.ppStory);
        return;
      }
      try {
        const state = history.state && typeof history.state === 'object' ? history.state : {};
        history.pushState({ ...state, ppStory: true }, '', window.location.pathname + window.location.search + hash);
        this.historyPushed = true;
      } catch (error) {
        this.syncHash(key);
      }
    }

    /* Selbst angelegten Verlaufseintrag wieder entfernen, sonst braucht "Zurueck" zwei Tipps */
    releaseHistory() {
      const pushed = this.historyPushed && history.state && history.state.ppStory;
      this.historyPushed = false;
      if (pushed) history.back();
      else this.syncHash('');
    }

    onListIntent = (event) => {
      const link = event.target.closest && event.target.closest('[data-story-link]');
      if (!link || this.preloaded.has(link)) return;
      const entry = this.entries.find((candidate) => candidate.link === link);
      if (!entry) return;
      this.preloaded.add(link);
      const [first, second] = entry.slides;
      [
        ...(first.tiles || []).map((tile) => tile.src),
        ...(first.people || []).map((person) => person.src),
        first.img,
        second && second.img,
      ]
        .filter(Boolean)
        .forEach((src) => {
          const image = new Image();
          image.src = src;
        });
    };

    /*
     * "Zuletzt angesehen": Horizon speichert die zuletzt besuchten Produkt-IDs (localStorage viewedProducts).
     * Die Produktdaten liefert sections/pp-story-recent.liquid ueber die Suche (wie Horizons Suchfeld).
     * Der Kreis bleibt verborgen, bis es Produkte gibt.
     */
    async loadRecent() {
      const item = this.list && this.list.querySelector('[data-recent]');
      const link = item && item.querySelector('[data-story-link]');
      if (!link) return;
      let ids = [];
      try {
        ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
      } catch (error) {
        ids = [];
      }
      ids = (Array.isArray(ids) ? ids : []).map(String).filter((id) => /^\d+$/.test(id)).slice(0, 8);
      if (ids.length === 0) return;

      let raw = [];
      try {
        const searchUrl = (window.Theme && window.Theme.routes && window.Theme.routes.search_url) || '/search';
        const url = new URL(searchUrl, window.location.origin);
        url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
        url.searchParams.set('type', 'product');
        url.searchParams.set('section_id', 'pp-story-recent');
        const response = await fetch(url, { credentials: 'same-origin' });
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        raw = JSON.parse(doc.querySelector('[data-recent-products]').textContent);
      } catch (error) {
        return;
      }
      if (!this.isConnected) return;

      const byId = new Map(raw.map((product) => [String(product.id), product]));
      const products = ids.map((id) => byId.get(id)).filter((product) => product && product.img).map((product) => this.productFromRaw(product));
      if (products.length === 0) return;

      const story = {
        key: 'zuletzt-angesehen',
        recent: true,
        title: item.dataset.label,
        url: link.getAttribute('href'),
        avatar: products[0].mini,
        products,
      };
      const slides = products.map((product) => ({ ...product, product: true, cta: this.dataset.ctaLabel }));
      this.storyCache.set(link, { story, slides, stamp: stampOf(slides) });

      const holder = item.querySelector('.pp-story__img');
      if (holder && !holder.querySelector('img')) {
        holder.classList.remove('pp-story__img--emoji');
        holder.replaceChildren(createImage(products[0].thumb || products[0].img, ''));
      }
      item.hidden = false;
      this.collectEntries();
      this.openFromHash();
    }

    /* Gleiche Regeln wie in Liquid: Warenkorb nur ohne Gestaltung, Badges Bestseller / Neu */
    productFromRaw(raw) {
      const tags = Array.isArray(raw.tags) ? raw.tags : [];
      const title = raw.title || '';
      const keywords = (this.dataset.designKeywords || '')
        .split(',')
        .map((keyword) => keyword.trim())
        .filter(Boolean);
      const needsDesign = keywords.some((keyword) => tags.includes(keyword) || title.includes(keyword));
      const badges = [];
      if ((this.dataset.bestsellerIds || '').includes(`,${raw.id},`)) badges.push(this.dataset.bestsellerLabel);
      const newTag = this.dataset.newTag;
      const newSeconds = Number(this.dataset.newSeconds) || 0;
      const isNew =
        (newTag && tags.includes(newTag)) || (newSeconds > 0 && raw.created && Date.now() / 1000 - Number(raw.created) < newSeconds);
      if (isNew) badges.push(this.dataset.newProductLabel);
      return {
        title,
        url: raw.url,
        price: raw.price,
        img: raw.img,
        thumb: raw.thumb,
        mini: raw.mini,
        alt: raw.alt,
        variant: needsDesign ? 0 : Number(raw.variant) || 0,
        badges: badges.filter(Boolean),
        production: raw.production,
      };
    }

    /* Teilbare Links: #story=<Kollektions-Handle> oeffnet die Story direkt (Instagram-Bio, Newsletter) */
    openFromHash() {
      const match = window.location.hash.match(/^#story=([^&]+)/);
      if (!match || !this.dialog || !this.refs) return;
      let key = match[1];
      try {
        key = decodeURIComponent(key);
      } catch (error) {
        /* Ungueltige Kodierung: Rohwert verwenden */
      }
      const index = this.entries.findIndex((entry) => entry.story.key === key);
      if (index < 0) return;
      if (this.dialog.open && this.current === index) return;
      this.open(index);
    }

    /* Adresse zeigt die offene Story, damit man sie kopieren und teilen kann (ohne neuen Verlaufseintrag) */
    syncHash(key) {
      if (window.Shopify && window.Shopify.designMode) return;
      const hash = key ? `#story=${encodeURIComponent(key)}` : '';
      if (window.location.hash === hash) return;
      if (!key && !window.location.hash.startsWith('#story=')) return;
      try {
        history.replaceState(history.state, '', window.location.pathname + window.location.search + hash);
      } catch (error) {
        /* Nur Komfortfunktion */
      }
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
    }

    /* Sichtbare Stories in DOM-Reihenfolge einsammeln (nach der Saison-Korrektur erneut) */
    collectEntries() {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      const currentLink = this.dialog.open && this.entries[this.current] ? this.entries[this.current].link : null;
      const seen = readSeen();
      this.sortUnseenFirst(seen);
      this.entries = [];

      this.querySelectorAll('[data-story-link]').forEach((link) => {
        if (link.closest('.pp-stories__item').hidden) return;
        const data = this.storyData(link);
        if (!data) return;
        this.entries.push({ link, story: data.story, slides: data.slides, stamp: data.stamp });
        this.applySeen(link, data, seen);
      });

      if (currentLink) {
        const index = this.entries.findIndex((entry) => entry.link === currentLink);
        if (index >= 0) this.current = index;
      }
    }

    storyData(link) {
      if (!this.storyCache.has(link)) {
        const script = link.parentElement.querySelector('script[data-story]');
        /* Ohne Daten-Script (Zuletzt angesehen) erst cachen, wenn loadRecent die Daten hat */
        if (!script) return null;
        let story = null;
        try {
          story = JSON.parse(script.textContent);
        } catch (error) {
          story = null;
        }
        const slides = story ? this.buildSlides(story) : [];
        this.storyCache.set(link, slides.length ? { story, slides, stamp: stampOf(slides) } : null);
      }
      return this.storyCache.get(link);
    }

    /*
     * Wie bei Instagram: ungesehene Kollektions-Stories vor die gesehenen, einmal beim Laden.
     * Fest bleiben hervorgehobene, saisonale, Marken- und Zuletzt-angesehen-Stories sowie reine Links.
     */
    sortUnseenFirst(seen) {
      if (this.sortedOnce || !this.list) return;
      this.sortedOnce = true;
      if (this.dataset.unseenFirst === 'false' || (window.Shopify && window.Shopify.designMode)) return;

      const isSeen = (item) => {
        const data = this.storyData(item.querySelector('[data-story-link]'));
        const { key } = data.story;
        return seen.includes(`${key}:${data.stamp}`) || seen.includes(key);
      };
      const movable = Array.from(this.list.children).filter((item) => {
        if (item.hidden || item.hasAttribute('data-seasonal') || item.hasAttribute('data-recent')) return false;
        const link = item.querySelector('[data-story-link]');
        if (!link || link.classList.contains('pp-story--highlight')) return false;
        const data = this.storyData(link);
        return Boolean(data && !data.story.brand);
      });
      if (movable.length < 2) return;

      const sorted = [...movable.filter((item) => !isSeen(item)), ...movable.filter(isSeen)];
      if (sorted.every((item, index) => item === movable[index])) return;
      const markers = movable.map((item) => {
        const marker = document.createComment('');
        item.replaceWith(marker);
        return marker;
      });
      markers.forEach((marker, index) => marker.replaceWith(sorted[index]));
    }

    /*
     * Gesehen wird mit Stand gespeichert ("kissen:abc12"). Kommen neue Produkte in die Story,
     * wird der Ring wieder bunt und der Kreis zeigt "Neu" (sofern kein anderes Badge dasteht).
     * Alte Eintraege ohne Stand gelten als gesehen.
     */
    applySeen(link, data, seen) {
      const { key } = data.story;
      const stamped = `${key}:${data.stamp}`;
      const isSeen = seen.includes(stamped) || seen.includes(key);
      const wasSeenBefore = !isSeen && seen.some((value) => value.startsWith(`${key}:`));
      link.classList.toggle('pp-story--seen', isSeen);

      const badge = link.querySelector('[data-story-badge]');
      if (!badge || data.story.recent) return;
      if (wasSeenBefore && (badge.hidden || badge.dataset.newBadge)) {
        badge.textContent = this.dataset.newLabel || 'Neu';
        badge.dataset.newBadge = 'true';
        badge.hidden = false;
      } else if (!wasSeenBefore && badge.dataset.newBadge) {
        badge.hidden = true;
        delete badge.dataset.newBadge;
      }
    }

    markSeen(entry) {
      const { key } = entry.story;
      const stamped = `${key}:${entry.stamp}`;
      const seen = readSeen().filter((value) => value !== key && !value.startsWith(`${key}:`));
      seen.push(stamped);
      writeSeen(seen);
      this.applySeen(entry.link, entry, seen);
    }

    onListClick = (event) => {
      const link = event.target.closest('[data-story-link]');
      if (!link) return;
      const index = this.entries.findIndex((entry) => entry.link === link);
      if (index < 0) return;
      event.preventDefault();
      this.open(index);
    };

    /* Pause: Button/Leertaste (userPaused), Halten (holding), Tastaturfokus im Infobereich (focusPaused) */
    syncPause() {
      if (!this.dialog) return;
      const paused = Boolean(this.userPaused || this.holding || this.focusPaused);
      this.dialog.classList.toggle('is-paused', paused);
      this.dialog.classList.toggle('is-user-paused', Boolean(this.userPaused));
      const button = this.refs && this.refs.pause;
      if (button) {
        const label = this.userPaused ? button.dataset.labelPlay : button.dataset.labelPause;
        if (label) button.setAttribute('aria-label', label);
      }
    }

    togglePause() {
      this.userPaused = !this.userPaused;
      this.syncPause();
    }

    onFocusChange = () => {
      /* focusout feuert vor dem neuen focusin: den aktuellen Fokus erst im naechsten Tick pruefen */
      requestAnimationFrame(() => {
        const active = document.activeElement;
        this.focusPaused = Boolean(
          active &&
            this.dialog.contains(active) &&
            active.closest('.pp-sv__info') &&
            active.matches(':focus-visible')
        );
        this.syncPause();
      });
    };

    /* Screenreader: aktuelle Story und aktuelles Produkt hoeflich ansagen */
    announce(story, slide, index, total) {
      if (!this.refs.live) return;
      const parts = [story.title];
      if (slide.title && slide.title !== story.title) parts.push(slide.title);
      if (slide.price) parts.push(slide.price);
      this.refs.live.textContent = `${parts.join(', ')} (${index + 1} von ${total})`;
    }

    /* Einstiegsfolie (Collage der Kollektion) plus eine Folie je Produkt mit Bild */
    buildSlides(story) {
      if (story.brand) return this.buildBrandSlides(story);
      const products = (Array.isArray(story.products) ? story.products : []).filter((product) => product && product.img);
      const slides = products.map((product) => ({ ...product, product: true, cta: this.dataset.ctaLabel }));
      if (this.dataset.intro !== 'false') {
        /* Erste Kachel gross (gleiches Bild wie Folie 2, also schon im Cache), der Rest klein */
        const tiles =
          products.length >= 3
            ? products.slice(0, products.length >= 6 ? 6 : 3).map((product, index) => ({
                src: index === 0 ? product.img : product.thumb || product.img,
                title: product.title,
                slide: index + 1,
              }))
            : [];
        slides.unshift({
          intro: true,
          title: story.heading || story.title,
          text: story.text,
          url: story.url,
          cta: this.dataset.introCta,
          img: tiles.length ? '' : story.cover,
          tiles,
        });
      }
      return slides;
    }

    /* Marken-Story: Einstieg mit allen Gruendern, danach eine Folie je Person */
    buildBrandSlides(story) {
      const people = (Array.isArray(story.people) ? story.people : []).filter((person) => person && person.img);
      const slides = people.slice(0, 3).map((person) => ({
        title: person.title,
        text: person.text,
        img: person.img,
        alt: person.alt,
        url: story.url,
        cta: this.dataset.founderCta,
      }));
      slides.unshift({
        intro: true,
        title: story.heading || story.title,
        text: story.text,
        url: story.url,
        cta: this.dataset.brandCta,
        img: people.length >= 2 ? '' : (people[0] && people[0].img) || story.cover,
        people:
          people.length >= 2
            ? people.map((person, index) => ({ src: person.img, name: person.name, slide: index < 3 ? index + 1 : 0 }))
            : [],
      });
      return slides;
    }

    /* Saisonale Stories: nur im Zeitfenster vor dem Anlass sichtbar, mit Countdown-Badge */
    applyOccasions() {
      const core = window.ppDelivery;
      const list = this.list;
      if (!core || !core.findOccasion || !list) return;
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' })
        .format(new Date())
        .split('-')
        .map(Number);
      const today = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
      const seasonal = [];

      list.querySelectorAll('.pp-stories__item').forEach((item) => {
        const mode = item.dataset.occasion || 'auto';
        if (mode === 'none') return;
        const occasion = core.findOccasion(mode === 'auto' ? item.dataset.label : mode);
        if (!occasion) return;
        const days = Math.round((core.nextOccasionDate(occasion, today) - today) / 86400000);
        /* Verbindlich in beide Richtungen: Liquid-Stand kann aus dem Seiten-Cache stammen */
        item.hidden = days > (Number(item.dataset.occasionLead) || 42);
        item.toggleAttribute('data-seasonal', !item.hidden);
        if (item.hidden) return;
        const badge = item.querySelector('[data-story-badge]');
        if (badge) {
          badge.textContent = days === 0 ? 'Heute' : days === 1 ? 'Morgen' : `noch ${days} Tage`;
          badge.hidden = false;
        }
        seasonal.push({ item, days });
      });

      /* Gleiche Reihenfolge wie in Liquid: hinter der letzten hervorgehobenen Story, naechster Anlass zuerst */
      const highlights = Array.from(list.children).filter((li) => li.querySelector('.pp-story--highlight'));
      const anchor = highlights[highlights.length - 1];
      const ordered = seasonal.sort((a, b) => a.days - b.days).map(({ item }) => item);
      if (ordered.length === 0) return;
      const children = Array.from(list.children);
      const start = anchor ? children.indexOf(anchor) + 1 : 0;
      const inPlace = ordered.every((item, index) => children[start + index] === item);
      if (inPlace) return;
      if (anchor) anchor.after(...ordered);
      else list.prepend(...ordered);
    }

    open(index, slide = 0) {
      const entry = this.entries[index];
      if (!entry) return;
      this.current = index;
      this.slide = slide;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.dialog.classList.toggle('pp-sv--manual', this.reduceMotion);

      const { story, slides } = entry;
      this.refs.storyTitle.textContent = story.title;
      if (story.avatar) {
        this.refs.avatar.hidden = false;
        this.refs.avatar.src = story.avatar;
      } else {
        this.refs.avatar.hidden = true;
        this.refs.avatar.removeAttribute('src');
      }
      this.refs.all.href = story.url;
      this.refs.all.textContent = `${(this.dataset.allLabel || 'Alle [title] ansehen').replace('[title]', story.title)} →`;
      this.refs.bars.replaceChildren(
        ...slides.map(() => {
          const bar = document.createElement('span');
          bar.className = 'pp-sv__bar';
          bar.append(document.createElement('i'));
          return bar;
        })
      );
      this.renderMore(slides);

      const opening = !this.dialog.open;
      if (opening) {
        this.dialog.showModal();
        /* Startfokus auf Pause, damit die Leertaste sofort anhaelt (ohne Auto-Weiter: Schliessen) */
        const startFocus = this.reduceMotion ? this.dialog.querySelector('[data-close]') : this.refs.pause;
        if (startFocus) startFocus.focus({ preventScroll: true });
      }
      this.render();

      this.markSeen(entry);
      if (opening) this.pushHistory(story.key);
      else this.syncHash(story.key);
      track('pp_story_open', { story: story.key });
    }

    /* Desktop: Vorschaubilder aller Produkte der Story zum direkten Anspringen */
    renderMore(slides) {
      const buttons = [];
      slides.forEach((slide, index) => {
        if (slide.intro) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pp-sv__mini pp-focus';
        button.dataset.slide = index;
        button.setAttribute('aria-label', slide.title || '');
        const img = createImage(slide.mini || slide.img, '');
        img.addEventListener('error', () => button.remove(), { once: true });
        button.append(img);
        button.addEventListener('click', () => this.goTo(index));
        buttons.push(button);
      });
      this.refs.moreList.replaceChildren(...buttons);
      this.refs.more.hidden = buttons.length < 2;
    }

    renderStage(slide) {
      if (slide.people && slide.people.length >= 2) {
        const row = document.createElement('div');
        row.className = 'pp-sv__people';
        slide.people.forEach((person, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pp-sv__person';
          button.style.animationDelay = `${index * 110}ms`;
          const frame = document.createElement('span');
          frame.className = 'pp-sv__person-img';
          frame.append(createImage(person.src, ''));
          const name = document.createElement('span');
          name.className = 'pp-sv__person-name';
          name.textContent = person.name || '';
          button.append(frame, name);
          if (person.slide) {
            button.addEventListener('click', () => this.handleZone(() => this.goTo(person.slide)));
          } else {
            button.tabIndex = -1;
          }
          row.append(button);
        });
        return [row];
      }
      if (slide.tiles && slide.tiles.length >= 3) {
        const grid = document.createElement('div');
        grid.className = `pp-sv__grid${slide.tiles.length < 6 ? ' pp-sv__grid--3' : ''}`;
        slide.tiles.forEach((item, index) => {
          const tile = document.createElement('button');
          tile.type = 'button';
          tile.className = 'pp-sv__tile';
          tile.style.animationDelay = `${index * 80}ms`;
          tile.setAttribute('aria-label', item.title || '');
          tile.append(createImage(item.src, ''));
          /* Tippen springt zur Produktfolie; nach einem Wischen nicht (handleZone) */
          tile.addEventListener('click', () => this.handleZone(() => this.goTo(item.slide)));
          grid.append(tile);
        });
        return [grid];
      }
      return slide.img ? [createImage(slide.img, slide.alt, 'pp-sv__photo')] : [];
    }

    render() {
      const { slides } = this.entries[this.current];
      const slide = slides[this.slide];
      const refs = this.refs;
      this.dialog.classList.toggle('pp-sv--intro', Boolean(slide.intro));

      const backdrop = (slide.tiles && slide.tiles[0] && slide.tiles[0].src) || (slide.people && slide.people[0] && slide.people[0].src) || slide.img;
      refs.backdrop.hidden = !backdrop;
      if (backdrop) refs.backdrop.src = backdrop;
      else refs.backdrop.removeAttribute('src');
      refs.stage.replaceChildren(...this.renderStage(slide));

      refs.title.textContent = slide.title || '';
      refs.text.textContent = slide.text || '';
      refs.text.hidden = !slide.text;
      refs.price.textContent = slide.price || '';
      refs.price.hidden = !slide.price;
      refs.cta.href = slide.url;
      refs.ctaLabel.textContent = slide.cta || '';
      const currentStory = this.entries[this.current].story;
      refs.all.hidden = Boolean(slide.intro || currentStory.brand || currentStory.recent);
      const deadline = slide.intro ? this.deadlineText(this.entries[this.current]) : '';
      refs.deadline.hidden = !deadline;
      if (deadline) {
        refs.deadline.innerHTML =
          '<svg viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="10" cy="10" r="7.5"/><path d="M10 6v4l2.5 2"/></svg>';
        const text = document.createElement('span');
        text.textContent = deadline;
        refs.deadline.append(text);
      }
      refs.moreList.querySelectorAll('[data-slide]').forEach((button) => {
        button.classList.toggle('is-active', Number(button.dataset.slide) === this.slide);
      });

      Array.from(refs.bars.children).forEach((bar, index) => {
        bar.classList.remove('is-active');
        bar.classList.toggle('is-done', index < this.slide);
      });
      const active = refs.bars.children[this.slide];
      void active.offsetWidth;
      active.classList.add('is-active');
      /* Pause vom Button bleibt ueber Folienwechsel erhalten */
      this.syncPause();
      this.renderBadges(slide);
      this.renderAdd(slide);
      this.announce(this.entries[this.current].story, slide, this.slide, slides.length);

      const upcoming = slides[this.slide + 1];
      if (upcoming && upcoming.img) {
        const preload = new Image();
        preload.src = upcoming.img;
      }
    }

    /* Bestseller / Neu aus Liquid, dazu der Liefertermin aus dem gemeinsamen Liefer-Kern */
    renderBadges(slide) {
      const container = this.refs.badges;
      const badges = [];
      if (slide.product) {
        (slide.badges || []).filter(Boolean).forEach((text) => {
          const badge = document.createElement('span');
          badge.className = 'pp-sv__badge';
          badge.textContent = text;
          badges.push(badge);
        });
        const delivery = this.deliveryText(slide);
        if (delivery) {
          const badge = document.createElement('span');
          badge.className = 'pp-sv__badge pp-sv__badge--delivery';
          badge.innerHTML =
            '<svg viewBox="0 0 20 20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M2 5h10v8H2zM12 8h3.5L18 11v2h-6"/><circle cx="5.5" cy="14.5" r="1.5"/><circle cx="14.5" cy="14.5" r="1.5"/></svg>';
          badge.append(delivery);
          badges.push(badge);
        }
      }
      container.replaceChildren(...badges);
      container.hidden = badges.length === 0;
    }

    /*
     * Saison-Stories: letzter Bestelltag, an dem die Lieferung (spaetestes Datum) noch am Tag vor dem
     * Anlass ankommt (gleiche Regel wie activeOccasion im Liefer-Kern). Heute zu spaet: kein Hinweis.
     */
    deadlineText(entry) {
      const core = window.ppDelivery;
      const label = this.dataset.deadlineLabel;
      if (!core || !core.findOccasion || !core.compute || !label || this.dataset.cutoff === undefined) return '';
      const item = entry.link.closest('.pp-stories__item');
      const mode = (item && item.dataset.occasion) || 'auto';
      if (!item || mode === 'none' || entry.story.brand || entry.story.recent) return '';
      const occasion = core.findOccasion(mode === 'auto' ? item.dataset.label : mode);
      if (!occasion) return '';

      try {
        if (!this.deliveryConfig) this.deliveryConfig = core.readConfig(this);
        const config = this.deliveryConfig;
        const DAY = 86400000;
        const addDays = (date, days) => new Date(date.getTime() + days * DAY);
        const isWorkingDay = (date, allowSaturday) => {
          const weekday = date.getUTCDay();
          if (weekday === 0 || (weekday === 6 && !allowSaturday)) return false;
          return !config.holidays.has(core.toIso(date));
        };
        const addWorkingDays = (date, count, allowSaturday) => {
          let result = date;
          let remaining = count;
          while (remaining > 0) {
            result = addDays(result, 1);
            if (isWorkingDay(result, allowSaturday)) remaining -= 1;
          }
          return result;
        };
        /* Bestellung an Tag X vor Bestellschluss: spaetester Liefertag */
        const latestFor = (day) => {
          let start = day;
          while (!isWorkingDay(start, config.shipSaturday)) start = addDays(start, 1);
          const ship = addWorkingDays(start, config.production, config.shipSaturday);
          return addWorkingDays(ship, config.transitMax, config.deliverSaturday);
        };

        const now = core.compute(config);
        const occasionDate = core.nextOccasionDate(occasion, now.today);
        const arriveBy = addDays(occasionDate, -1);
        if (now.latest > arriveBy) return '';

        let lastDay = now.beforeCutoff ? now.today : null;
        for (let day = addDays(now.today, 1); day <= arriveBy; day = addDays(day, 1)) {
          if (latestFor(day) > arriveBy) break;
          lastDay = day;
        }
        if (!lastDay) return '';

        const isToday = core.toIso(lastDay) === core.toIso(now.today);
        const cutoff = isWorkingDay(lastDay, config.shipSaturday) ? `, ${config.cutoff} Uhr` : '';
        const date = isToday ? `heute${cutoff}` : `${core.formatDate(lastDay)}${cutoff}`;
        return label.replace('[date]', date).replace('[occasion]', occasion.label);
      } catch (error) {
        return '';
      }
    }

    deliveryText(slide) {
      const core = window.ppDelivery;
      const label = this.dataset.deliveryLabel;
      if (!core || !core.compute || !label || this.dataset.cutoff === undefined) return '';
      try {
        if (!this.deliveryConfig) this.deliveryConfig = core.readConfig(this);
        const production = Number(slide.production);
        const config =
          slide.production !== '' && slide.production != null && Number.isFinite(production)
            ? { ...this.deliveryConfig, production }
            : this.deliveryConfig;
        const result = core.compute(config);
        return label.replace('[date]', core.formatDate(result.latest));
      } catch (error) {
        return '';
      }
    }

    renderAdd(slide) {
      const button = this.refs.add;
      const variant = slide.product && this.dataset.addLabel ? Number(slide.variant) : 0;
      button.hidden = !variant;
      this.refs.cta.classList.toggle('pp-btn--primary', !variant);
      this.refs.cta.classList.toggle('pp-btn--secondary', Boolean(variant));
      if (!variant) return;
      const added = this.added.has(variant);
      button.dataset.variantId = variant;
      button.disabled = added;
      button.toggleAttribute('data-added', added);
      button.removeAttribute('aria-busy');
      this.refs.addLabel.textContent = added ? this.dataset.addedLabel : this.dataset.addLabel;
    }

    /* Wie der Karten-Upsell: /cart/add.js plus Horizons CartLinesUpdateEvent, damit Zaehler und
       Warenkorb-Drawer sich aktualisieren. Das Event kommt aus dem offenen Story-Dialog, deshalb
       oeffnet Horizon den Drawer erst, wenn die Story geschlossen wird. */
    async addToCart() {
      const button = this.refs.add;
      const variantId = Number(button.dataset.variantId);
      if (!variantId || button.disabled) return;
      const entry = this.entries[this.current];
      const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      let CartLinesUpdateEvent = null;
      let deferred = null;
      try {
        ({ CartLinesUpdateEvent } = await import('@shopify/events'));
        deferred = CartLinesUpdateEvent.createPromise();
        button.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: String(variantId), quantity: 1 }],
            promise: deferred.promise,
          })
        );
      } catch (error) {
        CartLinesUpdateEvent = null;
        deferred = null;
      }

      let added = false;
      let sections;
      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message || 'add failed');
        added = true;
        sections = data.sections;
        this.added.add(variantId);
        if (entry) track('pp_story_add_to_cart', { story: entry.story.key, variantId });
      } catch (error) {
        console.error('[pp-stories]', error);
      }

      /* Nur die Folie aktualisieren, die noch zu diesem Produkt gehoert */
      if (Number(button.dataset.variantId) === variantId) {
        button.removeAttribute('aria-busy');
        if (added) {
          button.setAttribute('data-added', '');
          this.refs.addLabel.textContent = this.dataset.addedLabel;
        } else {
          button.disabled = false;
          this.refs.addLabel.textContent = this.dataset.errorLabel;
        }
      }
      if (this.refs.live) this.refs.live.textContent = added ? this.dataset.addedLabel : this.dataset.errorLabel;

      /* Event-Promise immer aufloesen (bei Fehler mit didError), nie verwerfen */
      if (deferred && CartLinesUpdateEvent) {
        let cart = null;
        try {
          const cartUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
          const response = await fetch(`${cartUrl}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
          cart = await response.json();
        } catch (error) {
          cart = null;
        }
        const cartIcon = document.querySelector('cart-icon');
        const knownCount = cartIcon ? Number(cartIcon.currentCartCount) : NaN;
        deferred.resolve({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
          detail: {
            items: cart ? cart.items : [],
            source: 'pp-stories',
            itemCount: cart ? cart.item_count : Number.isFinite(knownCount) ? knownCount + (added ? 1 : 0) : undefined,
            sections,
            didError: !added,
          },
        });
      }
    }

    goTo(index) {
      const { slides } = this.entries[this.current];
      if (!slides[index]) return;
      this.slide = index;
      this.render();
    }

    next() {
      const { slides } = this.entries[this.current];
      if (this.slide < slides.length - 1) {
        this.slide += 1;
        this.render();
      } else if (this.current < this.entries.length - 1) {
        this.open(this.current + 1);
      } else {
        this.close();
      }
    }

    prev() {
      if (this.slide > 0) {
        this.slide -= 1;
        this.render();
      } else if (this.current > 0) {
        const previous = this.entries[this.current - 1];
        this.open(this.current - 1, previous.slides.length - 1);
      } else {
        this.render();
      }
    }

    close() {
      if (!this.dialog.open) return;
      this.releaseHistory();
      this.dialog.close();
    }

    handleZone(action) {
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      action();
    }

    /* Gesten: Finger, Maus und Stift ueber Pointer Events */
    onPointerDown = (event) => {
      this.suppressClick = false;
      if (event.target.closest('a, button:not(.pp-sv__zone):not(.pp-sv__tile):not(.pp-sv__person)')) return;
      this.gesture = { x: event.clientX, y: event.clientY, time: Date.now() };
      this.holding = true;
      this.syncPause();
    };

    onPointerUp = (event) => {
      this.holding = false;
      this.syncPause();
      if (!this.gesture) return;
      const dx = event.clientX - this.gesture.x;
      const dy = event.clientY - this.gesture.y;
      const held = Date.now() - this.gesture.time;
      this.gesture = null;

      if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
        this.suppressClick = true;
        if (dx < 0) this.next();
        else this.prev();
      } else if (dy > CLOSE_PX && Math.abs(dy) > Math.abs(dx)) {
        this.suppressClick = true;
        this.close();
      } else {
        this.suppressClick = held > HOLD_MS;
      }
    };

    onPointerCancel = () => {
      this.gesture = null;
      this.holding = false;
      this.syncPause();
    };

    /* Trackpad: horizontales Wischen mit zwei Fingern */
    onWheel = (event) => {
      if (Math.abs(event.deltaX) < WHEEL_PX || Math.abs(event.deltaX) < Math.abs(event.deltaY)) return;
      const now = Date.now();
      if (this.wheelLock && now < this.wheelLock) return;
      this.wheelLock = now + WHEEL_LOCK_MS;
      if (event.deltaX > 0) this.next();
      else this.prev();
    };

    onKeyDown = (event) => {
      if ((event.key === ' ' || event.key === 'Spacebar') && !this.reduceMotion) {
        /* Leertaste = Pause/Fortsetzen, auch wenn ein anderer Button den Fokus hat (Enter loest Buttons aus).
           Auf dem Pause-Button selbst uebernimmt der native Klick. */
        if (event.target === this.refs.pause) return;
        event.preventDefault();
        if (!event.repeat) this.togglePause();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      }
    };
  }

  customElements.define('pp-stories', PpStories);
}
